import { Select, SelectItem } from '@heroui/react';
import { Check } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetGastronomyPoints } from '@/views/RoomService/queries';

import { changeKdsOutlet, KdsAssignmentValue, resolveKdsOutletId } from './kdsAssignment';
import * as S from './KdsAssignmentFields.styles';

/** Klucz opcji „Wszystkie” (HeroUI nie przyjmuje pustego klucza). */
const ALL_KEY = '__all__';

interface Option {
  id: string;
  name: string;
  /** Kolor stanowiska; undefined = opcja bez kropki (punkt albo „Wszystkie”). */
  color?: string | null;
  inactive?: boolean;
}

interface CheckSelectProps {
  labelId: string;
  options: Option[];
  value: string;
  onChange: (id: string) => void;
  isDisabled?: boolean;
  inactiveLabel: string;
}

const renderOptionLabel = (option: Option, inactiveLabel: string): ReactNode => (
  <S.OptionLabel>
    {option.color !== undefined && <S.StationDot $color={option.color} aria-hidden />}
    <span className="truncate">{option.name}</span>
    {option.inactive && <S.OptionMuted>{inactiveLabel}</S.OptionMuted>}
  </S.OptionLabel>
);

/** Dropdown z jednym wyborem i checkboxem przy każdej opcji (wzór z parowania KDS). */
const CheckSelect: FC<CheckSelectProps> = ({
  labelId, options, value, onChange, isDisabled, inactiveLabel,
}) => {
  const selectedKey = value || ALL_KEY;
  return (
    <Select
      aria-labelledby={labelId}
      selectionMode="single"
      disallowEmptySelection
      radius="sm"
      variant="bordered"
      isDisabled={isDisabled}
      selectedKeys={[selectedKey]}
      onSelectionChange={(keys) => {
        const next = String(Array.from(keys as Iterable<string | number>)[0] ?? ALL_KEY);
        onChange(next === ALL_KEY ? '' : next);
      }}
      renderValue={(items) => items.map((item) => {
        const option = options.find((o) => o.id === item.key);
        return option ? <span key={option.id}>{renderOptionLabel(option, inactiveLabel)}</span> : null;
      })}
      classNames={{
        trigger: '!h-[44px] min-h-[44px] bg-white dark:bg-[#111827] border-1 border-border-strong rounded-8 shadow-none data-[hover=true]:border-gray-400',
        value: 'text-[15px] text-text-primary',
        popoverContent: 'rounded-8 p-1',
      }}
    >
      {options.map((option) => {
        const isSelected = selectedKey === option.id;
        return (
          <SelectItem
            key={option.id}
            textValue={option.name}
            hideSelectedIcon
            className="rounded-4 text-[14px]"
            startContent={(
              <span
                aria-hidden
                className={`flex items-center justify-center w-[18px] h-[18px] rounded-4 border-2 shrink-0 ${isSelected
                  ? 'bg-accent-ink border-accent-ink text-white' : 'border-[#9ca3af] bg-white'}`}
              >
                {isSelected && <Check size={12} strokeWidth={3} />}
              </span>
            )}
          >
            {renderOptionLabel(option, inactiveLabel)}
          </SelectItem>
        );
      })}
    </Select>
  );
};

interface KdsAssignmentFieldsProps {
  hotelId: string;
  roomServices: { id: string; name: string }[];
  value: KdsAssignmentValue;
  onChange: (next: KdsAssignmentValue) => void;
  isDisabled?: boolean;
  /** Prefiks id pól, gdy na stronie są dwa formularze (strona + modal). */
  idPrefix?: string;
  /** Ekran ma przypisane stanowisko, którego już nie ma w room service. */
  hasDeletedStation?: boolean;
  /** Gdy nie ma czego wybrać (jeden punkt bez stanowisk), pokaż o tym zdanie (modal edycji). */
  showEmptyHint?: boolean;
}

/**
 * Wybór punktu gastronomicznego i stanowiska dla ekranu KDS, wspólny dla parowania
 * i zmiany przypisania. Stanowisko da się wybrać dopiero, gdy jest punkt (stanowiska
 * należą do punktu), a zmiana punktu wraca do „Wszystkie stanowiska”.
 */
export const KdsAssignmentFields: FC<KdsAssignmentFieldsProps> = ({
  hotelId, roomServices, value, onChange, isDisabled, idPrefix = 'kds', hasDeletedStation, showEmptyHint,
}) => {
  const { t } = useTranslation();
  const effectiveOutletId = resolveKdsOutletId(value.outletId, roomServices);
  const { data: stations = [], isLoading: isLoadingStations, isError: isStationsError } = useGetGastronomyPoints(
    hotelId,
    effectiveOutletId || null,
  );

  const inactiveLabel = String(t('myDevices.kds.stationInactive', 'wyłączone'));
  const outletLabelId = `${idPrefix}-outlet-label`;
  const stationLabelId = `${idPrefix}-station-label`;

  // Ekran może wskazywać punkt usunięty w room service: pokazujemy go jako opcję, żeby dało się go zmienić.
  const isOutletMissing = !!value.outletId && !roomServices.some((outlet) => outlet.id === value.outletId);
  const outletOptions: Option[] = [
    { id: ALL_KEY, name: String(t('myDevices.kds.outletAll', 'Wszystkie punkty')) },
    ...roomServices.map((outlet) => ({ id: outlet.id, name: outlet.name })),
    ...(isOutletMissing
      ? [{ id: value.outletId, name: String(t('myDevices.kds.outletDeleted', 'Punkt usunięty')) }]
      : []),
  ];
  const stationOptions: Option[] = [
    { id: ALL_KEY, name: String(t('myDevices.kds.stationAll', 'Wszystkie stanowiska')) },
    ...stations
      .filter((point): point is typeof point & { id: string } => !!point.id)
      .map((point) => ({
        id: point.id,
        name: point.name,
        color: point.color ?? null,
        inactive: point.isActive === false,
      })),
  ];

  // Pole stanowiska pokazujemy, gdy jest z czego wybierać albo trzeba wyczyścić usunięte.
  const showStations = !!effectiveOutletId
    && (isStationsError || stationOptions.length > 1 || !!value.stationId || !!hasDeletedStation);

  const showOutlets = roomServices.length > 1 || isOutletMissing;
  if (showEmptyHint && !showOutlets && !showStations && !isLoadingStations) {
    return (
      <S.Hint>
        {t('myDevices.kds.assignmentEmpty', 'W hotelu nie ma innych punktów ani stanowisk do wyboru.')}
      </S.Hint>
    );
  }

  return (
    <>
      {showOutlets && (
        <S.Field>
          <S.Label id={outletLabelId}>{t('myDevices.kds.outletLabel', 'Punkt gastronomiczny')}</S.Label>
          <CheckSelect
            labelId={outletLabelId}
            options={outletOptions}
            value={value.outletId}
            isDisabled={isDisabled}
            inactiveLabel={inactiveLabel}
            onChange={(outletId) => onChange(changeKdsOutlet(value, outletId))}
          />
          <S.Hint>{t('myDevices.kds.outletHint', 'Ekran pokaże tylko zamówienia z wybranego punktu.')}</S.Hint>
        </S.Field>
      )}

      {showStations && (
        <S.Field>
          <S.Label id={stationLabelId}>{t('myDevices.kds.stationLabel', 'Stanowisko')}</S.Label>
          {isStationsError ? (
            <S.Warning role="alert">
              {t(
                'myDevices.kds.errors.stationsLoadFailed',
                'Nie udało się wczytać stanowisk. Punkt możesz zapisać, a stanowisko wybrać później.',
              )}
            </S.Warning>
          ) : (
            <>
              <CheckSelect
                labelId={stationLabelId}
                options={stationOptions}
                value={value.stationId}
                isDisabled={isDisabled || isLoadingStations}
                inactiveLabel={inactiveLabel}
                onChange={(stationId) => onChange({ ...value, stationId })}
              />
              {hasDeletedStation && !value.stationId ? (
                <S.Warning>
                  {t(
                    'myDevices.kds.stationDeletedHint',
                    'Przypisane stanowisko zostało usunięte. Zapisz, aby ekran pokazywał wszystkie stanowiska, albo wybierz inne.',
                  )}
                </S.Warning>
              ) : (
                <S.Hint>
                  {t(
                    'myDevices.kds.stationHint',
                    'Ekran pokaże tylko pozycje przypisane do stanowiska. „Wszystkie stanowiska” to ekran wydawki.',
                  )}
                </S.Hint>
              )}
            </>
          )}
        </S.Field>
      )}
    </>
  );
};
